import { CourseReview } from '../../../mocks/courses';

interface Props {
  reviews: CourseReview[];
  rating: number;
  totalReviews: number;
}

const RatingBar = ({ label, value, total }: { label: string; value: number; total: number }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-slate-400 w-8 text-right whitespace-nowrap">{label} ★</span>
    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-amber-400 rounded-full transition-all duration-700"
        style={{ width: `${(value / total) * 100}%` }}
      />
    </div>
    <span className="text-xs text-slate-400 w-8 whitespace-nowrap">{value}</span>
  </div>
);

const CourseReviews = ({ reviews, rating, totalReviews }: Props) => (
  <div>
    <h2 className="text-xl font-bold text-[#0A1628] mb-5">學員評價</h2>

    <div className="flex gap-8 items-start mb-8">
      {/* Big rating */}
      <div className="flex-shrink-0 text-center bg-amber-50 rounded-2xl px-8 py-6 border border-amber-100">
        <div className="text-5xl font-bold text-[#0A1628] mb-1">{rating}</div>
        <div className="flex items-center justify-center gap-0.5 mb-1.5">
          {[1,2,3,4,5].map((s) => (
            <i key={s} className={`ri-star-${s <= Math.floor(rating) ? 'fill' : 'line'} text-amber-400 text-base`} />
          ))}
        </div>
        <div className="text-xs text-slate-400">課程總評分</div>
        <div className="text-xs text-slate-400 mt-0.5">{totalReviews.toLocaleString()} 則評價</div>
      </div>

      {/* Rating distribution */}
      <div className="flex-1 space-y-2.5 pt-2">
        {[
          { label: '5', value: Math.round(totalReviews * 0.78) },
          { label: '4', value: Math.round(totalReviews * 0.16) },
          { label: '3', value: Math.round(totalReviews * 0.04) },
          { label: '2', value: Math.round(totalReviews * 0.01) },
          { label: '1', value: Math.round(totalReviews * 0.01) },
        ].map((item) => (
          <RatingBar key={item.label} label={item.label} value={item.value} total={totalReviews} />
        ))}
      </div>
    </div>

    {/* Review cards */}
    <div className="space-y-4">
      {reviews.map((review) => (
        <div key={review.id} className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 bg-slate-100">
              <img
                src={review.avatar}
                alt={review.name}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#0A1628] text-sm">{review.name}</span>
                  <span className="text-xs text-slate-400">· {review.date}</span>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className={`ri-star-${s <= review.rating ? 'fill' : 'line'} text-amber-400 text-xs`} />
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-slate-400">{review.company}</span>
                <span className="text-slate-200">·</span>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-check-line text-xs" />
                  {review.result}
                </span>
              </div>

              <p className="text-slate-500 text-sm leading-relaxed">{review.content}</p>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* More reviews hint */}
    <div className="mt-5 text-center">
      <p className="text-xs text-slate-400">
        以上為精選評價，共 <span className="font-semibold text-[#0A1628]">{totalReviews.toLocaleString()}</span> 則學員評價
      </p>
    </div>
  </div>
);

export default CourseReviews;
